/**
 * Tier Limits Library
 *
 * Defines membership tier limits and feature access control for Culineer producers.
 *
 * Note: Basic tier is not included as it's homepage-only with no in-app access.
 */

// Tier configuration
export const TIER_LIMITS = {
  'verified business': {
    products: 8,
    canAddProducts: true,
    canAddManually: false,
  },
  'enhanced': {
    products: Infinity,
    canAddProducts: true,
    canAddManually: true,
  },
  'professional': {
    products: Infinity,
    canAddProducts: true,
    canAddManually: true,
  },
} as const

export type TierKey = keyof typeof TIER_LIMITS

export interface TierLimits {
  products: number
  canAddProducts: boolean
  canAddManually: boolean
}

/**
 * Get tier limits configuration for a given membership type
 *
 * @param membership_type - User's membership type (e.g., 'Verified Business', 'Enhanced', 'Professional')
 * @param enhanced - Whether the user has enhanced membership flag set
 * @param subscription_status - User's subscription status (optional, for upgraded users)
 * @returns Tier limits configuration
 */
export function getTierLimits(
  membership_type: string | null | undefined,
  enhanced: boolean,
  subscription_status?: string | null
): TierLimits {
  // enhanced flag (admin-granted or Stripe-set) OR an active/trialing subscription
  // both qualify for the enhanced tier.
  const hasActiveSub = subscription_status === 'active' || subscription_status === 'trialing'
  if (enhanced || hasActiveSub) {
    return TIER_LIMITS['enhanced']
  }

  // Normalize membership type
  if (!membership_type) {
    // Default to most restrictive if unknown
    return TIER_LIMITS['verified business']
  }

  const normalizedType = membership_type.toLowerCase() as TierKey

  // Return matching tier or default to verified business if unknown
  return TIER_LIMITS[normalizedType] || TIER_LIMITS['verified business']
}

/**
 * Check if user can add more products based on their tier and current count
 *
 * @param membership_type - User's membership type
 * @param currentCount - Current number of products
 * @param enhanced - Whether the user has enhanced membership
 * @param subscription_status - User's subscription status (optional)
 * @returns true if user can add more products
 */
export function canAddProducts(
  membership_type: string | null | undefined,
  currentCount: number,
  enhanced: boolean,
  subscription_status?: string | null
): boolean {
  const limits = getTierLimits(membership_type, enhanced, subscription_status)

  // Check if tier allows product addition
  if (!limits.canAddProducts) {
    return false
  }

  // Check if under the product limit
  return currentCount < limits.products
}

/**
 * Get remaining number of products that can be added
 *
 * @param membership_type - User's membership type
 * @param currentCount - Current number of products
 * @param enhanced - Whether the user has enhanced membership
 * @param subscription_status - User's subscription status (optional)
 * @returns Number of remaining products (Infinity for unlimited tiers)
 */
export function getRemainingProducts(
  membership_type: string | null | undefined,
  currentCount: number,
  enhanced: boolean,
  subscription_status?: string | null
): number {
  const limits = getTierLimits(membership_type, enhanced, subscription_status)

  if (limits.products === Infinity) {
    return Infinity
  }

  return Math.max(0, limits.products - currentCount)
}

/**
 * Check if user has reached their product limit
 *
 * @param membership_type - User's membership type
 * @param currentCount - Current number of products
 * @param enhanced - Whether the user has enhanced membership
 * @param subscription_status - User's subscription status (optional)
 * @returns true if user has reached their limit
 */
export function hasReachedProductLimit(
  membership_type: string | null | undefined,
  currentCount: number,
  enhanced: boolean,
  subscription_status?: string | null
): boolean {
  const limits = getTierLimits(membership_type, enhanced, subscription_status)

  if (limits.products === Infinity) {
    return false
  }

  return currentCount >= limits.products
}
